import type { NatsConnection } from "nats.ws";

import type { ApiSubscriber } from "@/natsstubs/wire/ApiSubscriber";
import type { SubjectScheme } from "@/natsstubs/wire/context";
import { ForbiddenPublisherError } from "@/natsstubs/wire/error";
import { NatsApiSubscriber } from "@/natsstubs/wire/nats/NatsApiSubscriber";
import { captureExceptionWithContext } from "@/sentry";

export interface EventSubscriberContext {
  company: string;
  user?: { id: string };
  natsConnection?: NatsConnection | null;
  natsScheme: SubjectScheme;
}

/**
 * A failed subscription is reported and otherwise ignored: the WAMP
 * subscription alongside it keeps the UI updating, so a missing NATS grant
 * must not take the session down with it.
 */
export function createEventSubscriber(context: EventSubscriberContext): ApiSubscriber | null {
  const connection = context.natsConnection;
  const userId = context.user?.id;
  if (!connection || !userId) {
    return null;
  }
  return new NatsApiSubscriber(
    {
      connection,
      userId,
      clientId: crypto.randomUUID(),
      defaultTimeout: 15000,
      scheme: context.natsScheme,
    },
    // a subscription is role-agnostic by contract (the wire layer strips a role
    // var), so pass only what resolving the subject needs
    { companyId: context.company },
    {
      onSubscribeError: (error, subject) =>
        captureExceptionWithContext(error, { subscription: { origin: "subscribe", subject } }),
      onHandleError: forbiddenPublisherReporter(),
    },
  );
}

/**
 * One misbehaving publisher would otherwise raise one Sentry event per event.
 * The seen-subjects set belongs to the subscriber, not the module: a reconnect
 * or relogin builds a new subscriber, and its drops are news again.
 */
function forbiddenPublisherReporter(): (error: unknown, subject: string) => void {
  const reported = new Set<string>();
  return (error, subject) => {
    if (error instanceof ForbiddenPublisherError) {
      if (reported.has(subject)) return;
      reported.add(subject);
    }
    captureExceptionWithContext(error, { subscription: { origin: "handle", subject } });
  };
}
